import React, { useCallback, useEffect } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { FaArrowLeft } from 'react-icons/fa';
import { toastSuccess } from 'react-geek-toast';
import { HiOutlineChevronDown } from 'react-icons/hi';
import { useHistory, useParams } from 'react-router-dom';
import disputeAlert from 'assets/images/icons/dispute-alert.svg';

import { createIconFromImage } from 'helper';
import { useRouterData } from 'partial/hooks';
import Button from 'partial/components/Button';
import FormInput from 'partial/form/FormInput';
import FormTextArea from 'partial/form/FormTextArea';
import Form, { ErrorLabel } from 'partial/form/Form';
import { useCreateModal } from 'partial/components/Modal';
import ConfirmationModal from 'partial/components/modals/ConfirmationModal';

import {
  useSelectedTransaction,
  useRequesetDisputeTransaction,
} from '../hooks';

const Icon = createIconFromImage({ url: disputeAlert });

const validationSchema = yup.object().shape({
  remarks: yup.string().when({
    is: (val) => !!val,
    then: (schema) => schema.max(120, 'Maximum of 120 characters'),
    otherwise: (schema) => schema.nullable(),
  }),
});

function DisputeTransaction() {
  const { uuid } = useParams();
  const history = useHistory();

  const [, updatedData] = useSelectedTransaction({
    id: uuid,
  });
  const [data] = useRouterData(updatedData);

  const {
    requestDisputeTransaction,
    isLoading,
  } = useRequesetDisputeTransaction({
    onSuccess: () => {
      history.push('/transactions');
      toastSuccess('Dispute request submitted.');
    },
  });

  const createModal = useCreateModal();

  const openConfirmationModal = useCallback(
    (formValues) => {
      createModal({
        content: (close) => (
          <ConfirmationModal
            title="Confirm Dispute"
            header={
              <>
                Are you sure you want to report this as dispute with{' '}
                <span className="text-black font-semibold">
                  {formValues?.refno}
                </span>
                ?
              </>
            }
            paragraph="If you continue, this will be tagged as dispute until it is resolved."
            firstBtnLabel="Cancel"
            secondBtnLabel="Confirm"
            secondBtnColor="primary"
            icon={Icon}
            onSubmit={() => {
              close();
              requestDisputeTransaction(formValues);
            }}
            onClose={close}
          />
        ),
      });
    },
    [createModal, requestDisputeTransaction]
  );

  const { handleSubmit, errors, setFieldValue, values, setValues } = useFormik({
    validationSchema,
    initialValues: data,
    onSubmit: openConfirmationModal,
  });

  useEffect(() => {
    setValues(data);
  }, [data, setValues]);

  return (
    <div>
      <div>
        <Button
          icon={FaArrowLeft}
          onClick={() => history.push('/transactions')}
        >
          Back
        </Button>
      </div>
      <div className="p-5 border-b text-lg font-medium">Dispute Details</div>
      <Form className="p-5 mt-5" onSubmit={handleSubmit} error={errors}>
        <div className="grid grid-cols-3 gap-4 ">
          <FormInput
            name="account_name"
            label="Subscriber Name"
            value={values?.account_name || ''}
            disabled
          />
          <FormInput
            name="date"
            label="Date"
            value={values?.created_at?.split(' ')?.[0] || ''}
            disabled
          />
          <FormInput
            name="time"
            label="Time"
            value={values?.created_at?.split(' ')?.[1] || ''}
            icon={HiOutlineChevronDown}
            disabled
          />
          <FormInput
            name="refno"
            label="Reference Number"
            value={values?.refno || ''}
            disabled
          />
          <FormInput
            name="transaction_fee"
            label="Transaction Fee"
            value={`B$ ${values?.transaction_fee || ''}`}
            disabled
          />
          <FormInput
            name="amount"
            label="Amount"
            value={`B$ ${values?.amount || ''}`}
            disabled
          />
          <FormInput
            name="other_charges"
            label="Other Charges"
            value={`B$ ${values?.other_charges || ''}`}
            disabled
          />
          <FormInput
            name="net_amount"
            label="Net Amount"
            value={`B$ ${values?.net_amount || ''}`}
            disabled
          />
          <FormInput name="status" label="Status" value="Success" disabled />
          <div className="col-span-2">
            <FormTextArea
              required
              label="Remarks"
              name="remarks"
              onSetFieldValue={setFieldValue}
              maxLength={120}
              value={values?.remarks ?? ''}
            />
            <ErrorLabel name="remarks" />
          </div>
        </div>

        <div className="flex mt-4 space-x-4">
          <Button
            outlinePrimary
            onClick={() => history.push('/transactions')}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button primary type="submit" className="w-56" isLoading={isLoading}>
            Confirm
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default DisputeTransaction;
